import { normalizeRelativePath } from "../domain/paths";
import type { WorkspaceEntry, WorkspaceGateway, WorkspacePermission } from "./workspace_gateway";

/** Identifies the browser's portable missing-entry failure. */
function isNotFound(error: unknown): boolean {
  return error instanceof DOMException && error.name === "NotFoundError";
}

/** Identifies a real entry queried through the opposite file/directory handle API. */
function isTypeMismatch(error: unknown): boolean {
  return error instanceof DOMException && error.name === "TypeMismatchError";
}

export interface WorkspacePermissionController {
  query(): Promise<WorkspacePermission>;
  request(): Promise<WorkspacePermission>;
}

export type DirectoryPicker = (options: {
  readonly id: string;
  readonly mode: "read";
  readonly startIn: FileSystemDirectoryHandle;
}) => Promise<FileSystemDirectoryHandle>;

/** Adapts a user-granted directory handle to Shotboard filesystem capabilities. */
export class FileSystemWorkspaceGateway implements WorkspaceGateway {
  public readonly name: string;
  private readonly handle: FileSystemDirectoryHandle;
  private readonly permissionRequired: boolean;
  private readonly permissionController: WorkspacePermissionController | null;
  private readonly directoryPicker: DirectoryPicker | null;

  /** Retains only the granted root handle and a safe display name. */
  public constructor(
    handle: FileSystemDirectoryHandle,
    options: {
      readonly name?: string;
      readonly directoryPicker?: DirectoryPicker | null;
      readonly permissionController?: WorkspacePermissionController;
      readonly permissionRequired?: boolean;
    } = {},
  ) {
    this.handle = handle;
    this.name = options.name ?? handle.name;
    this.permissionRequired = options.permissionRequired ?? true;
    this.permissionController = options.permissionController ?? null;
    this.directoryPicker =
      options.directoryPicker === undefined
        ? "showDirectoryPicker" in window
          ? window.showDirectoryPicker.bind(window)
          : null
        : options.directoryPicker;
  }

  /** Exposes the structured-cloneable handle for recent-workspace storage. */
  public get directoryHandle(): FileSystemDirectoryHandle {
    return this.handle;
  }

  /** Queries permission without triggering a browser prompt. */
  public async queryPermission(): Promise<WorkspacePermission> {
    if (this.permissionController !== null) {
      return this.permissionController.query();
    }
    if (!this.permissionRequired) {
      return "granted";
    }
    return this.handle.queryPermission({ mode: "readwrite" });
  }

  /** Requests permission only from a caller handling a direct user gesture. */
  public async requestPermission(): Promise<WorkspacePermission> {
    if (this.permissionController !== null) {
      return this.permissionController.request();
    }
    if (!this.permissionRequired) {
      return "granted";
    }
    return this.handle.requestPermission({ mode: "readwrite" });
  }

  /** Opens the public system directory picker at a resolved folder without claiming Finder reveal. */
  public async showDirectoryInPicker(path: readonly string[]): Promise<void> {
    if (this.directoryPicker === null) {
      throw new Error("This browser cannot show the folder in its system picker.");
    }
    const startIn = await this.resolveDirectory(path, false);
    await this.directoryPicker({
      id: "shotboard-folder-location",
      mode: "read",
      startIn,
    });
  }

  /** Lists direct native entries without exposing absolute paths. */
  public async listDirectory(path: readonly string[]): Promise<readonly WorkspaceEntry[]> {
    const directory = await this.resolveDirectory(path, false);
    const entries: WorkspaceEntry[] = [];

    for await (const handle of directory.values()) {
      entries.push({ kind: handle.kind, name: handle.name });
    }

    return entries.sort((left, right) => left.name.localeCompare(right.name));
  }

  /** Creates one directory beneath a verified existing parent. */
  public async createDirectory(path: readonly string[]): Promise<void> {
    const normalized = normalizeRelativePath(path);
    const name = normalized.at(-1);
    if (name === undefined) {
      throw new Error("The workspace root already exists.");
    }
    const parent = await this.resolveDirectory(normalized.slice(0, -1), false);
    await parent.getDirectoryHandle(name, { create: true });
  }

  /** Reads a native file into an owned byte buffer. */
  public async readFile(path: readonly string[]): Promise<Uint8Array<ArrayBuffer>> {
    const { parent, name } = await this.resolveFileParent(path);
    const file = await (await parent.getFileHandle(name)).getFile();
    return new Uint8Array(await file.arrayBuffer());
  }

  /** Uses createWritable's temporary-file swap and closes before reporting success. */
  public async writeFileSafely(
    path: readonly string[],
    bytes: Uint8Array<ArrayBuffer>,
  ): Promise<void> {
    const { parent, name } = await this.resolveFileParent(path);
    const file = await parent.getFileHandle(name, { create: true });
    const writable = await file.createWritable({ keepExistingData: false });

    try {
      await writable.write(bytes);
      await writable.close();
    } catch (error) {
      await writable.abort().catch(() => undefined);
      throw error;
    }
  }

  /** Checks a path without creating a missing entry. */
  public async fileExists(path: readonly string[]): Promise<boolean> {
    const normalized = normalizeRelativePath(path);
    const name = normalized.at(-1);
    if (name === undefined) {
      return true;
    }

    try {
      const parent = await this.resolveDirectory(normalized.slice(0, -1), false);
      await parent.getFileHandle(name);
      return true;
    } catch (error) {
      if (!isNotFound(error) && !isTypeMismatch(error)) {
        throw error;
      }
    }

    try {
      const parent = await this.resolveDirectory(normalized.slice(0, -1), false);
      await parent.getDirectoryHandle(name);
      return true;
    } catch (error) {
      if (isTypeMismatch(error)) {
        return true;
      }
      if (isNotFound(error)) {
        return false;
      }
      throw error;
    }
  }

  /** Removes one resolved child and never permits deleting the granted workspace root. */
  public async removeEntry(
    path: readonly string[],
    options: { readonly recursive: boolean },
  ): Promise<void> {
    const normalized = normalizeRelativePath(path);
    const name = normalized.at(-1);
    if (name === undefined) {
      throw new Error("The workspace root cannot be removed.");
    }
    const parent = await this.resolveDirectory(normalized.slice(0, -1), false);
    await parent.removeEntry(name, { recursive: options.recursive });
  }

  /** Traverses only normalized child handles beneath the granted root. */
  private async resolveDirectory(
    path: readonly string[],
    create: boolean,
  ): Promise<FileSystemDirectoryHandle> {
    let directory = this.handle;
    for (const segment of normalizeRelativePath(path)) {
      directory = await directory.getDirectoryHandle(segment, { create });
    }
    return directory;
  }

  /** Resolves a file's verified parent and final name. */
  private async resolveFileParent(path: readonly string[]): Promise<{
    readonly parent: FileSystemDirectoryHandle;
    readonly name: string;
  }> {
    const normalized = normalizeRelativePath(path);
    const name = normalized.at(-1);
    if (name === undefined) {
      throw new Error("A file path must contain a file name.");
    }
    const parent = await this.resolveDirectory(normalized.slice(0, -1), false);
    return { parent, name };
  }
}
